import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import DropdownList from '../DropdownList/DropdownList';
import useMenuOpenState from '../Hooks/useMenuOpenState';
import Button from '../Button/Button';
import UpIcon from '../../Icons/UpIcon';
import DownIcon from '../../Icons/DownIcon';
import { NOOP } from '../../../utils/const';

const cssBlock = 'v2-dropdown-button';

/**
Bottoni utilizzati per raggruppare azioni molto simili tra di loro.
L'etichetta deve riassumere la categoria a cui tutte le azioni della dropdown appartengono.

Per specificare la lista delle azioni da visualizzare nel menù
aggiungere come child di `DropdownButton` il componente `DropdownButton.DropdownList`.
Tipicamente i children di `DropdownButton.DropdownList` sono componenti `Button`.

_ATTENZIONE: Se non esiste al primo livello un child di tipo `DropdownButton.DropdownList` non sarà possibile identificare i componenti da inserire nel menù._

Gli altri children di `DropdownButton` sono tipicamente una `Icon` e un testo.
 */
const DropdownButton = (props) => {
  const {
    disabled, primary, small, vertical, left, showOpenClose,
    children, className, emphasis,
    onOpen: onDropdownOpen, onClose: onDropdownClose,
  } = props;

  const { open, onOpen } = useMenuOpenState({ onOpen: onDropdownOpen, onClose: onDropdownClose });

  const [dropDownItems, firstChild, lastChild] = useMemo(
    () => {
      const [ddi, buttonChildren] = DropdownList.extractDropDownList(children);
      if (vertical && buttonChildren && buttonChildren.length > 0) {
        const lastChildrenComp = buttonChildren.pop();
        return [ddi, buttonChildren, lastChildrenComp];
      }
      return [ddi, buttonChildren, undefined];
    },
    [children, vertical],
  );

  const classes = cns(
    cssBlock,
    {
      [`${cssBlock}--disabled`]: disabled,
      [`${cssBlock}--small`]: small,
      [`${cssBlock}--left`]: left,
    },
    className,
  );

  // in modalità vertical, open/close button è affiancato all'ultimo child.
  return (
    <div className={classes}>
      <Button
        className={`${cssBlock}__button`}
        emphasis={emphasis}
        disabled={disabled}
        small={small}
        primary={primary}
        vertical={vertical}
        onClick={open ? NOOP : onOpen} // la chiusura è intercettata dalla gestione del menù per ogni click
      >
        {firstChild}
        <div className={`${cssBlock}__toggleContainer`}>
          {lastChild}
          {showOpenClose && (
            <div className={`${cssBlock}__toggle`}>
              {open ? <UpIcon /> : <DownIcon />}
            </div>
          )}
        </div>
      </Button>
      {open && dropDownItems}
    </div>
  );
};

DropdownButton.propTypes = {
  /**
Se true, il bottone non genera alcun evento.
  */
  disabled: PropTypes.bool,

  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,

  /** Definisce l'azione principale */
  primary: PropTypes.bool,

  /**
Se true, il bottone viene mostrato in formato ridotto.
  */
  small: PropTypes.bool,

  /**
Se true, mostra l'icona che mostra se il dropdown è aperto/chiuso.
  */
  showOpenClose: PropTypes.bool,

  /** Presenta i `children` in verticale. */
  vertical: PropTypes.bool,

  /** Mostra gli elementi del drop down list verso sinistra. */
  left: PropTypes.bool,

  /** Children del componente. I children di tipo `DropdownButton.DropdownList` faranno parte del menù di drop down */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),

  /** Callback chiamata all'apertura del Dropdown */
  onOpen: PropTypes.func,

  /** Callback chiamata alla chiusura del Dropdown */
  onClose: PropTypes.func,

  /** Enfasi del bottone */
  emphasis: PropTypes.oneOf([Button.Emphasis.LOW, Button.Emphasis.MEDIUM, Button.Emphasis.HIGH]),
};

DropdownButton.defaultProps = {
  disabled: false,
  primary: false,
  small: false,
  vertical: false,
  left: false,
  showOpenClose: true,
  children: undefined,
  className: undefined,
  onOpen: NOOP,
  onClose: NOOP,
  emphasis: Button.Emphasis.LOW,
};

DropdownButton.DropdownList = DropdownList;

export default DropdownButton;
